/// <amd-module name="Agile/Board/Badge/Services/BoardBadgeService" />
import { format } from "VSS/Core/Util/String";
import { Services, VssService, IVssService } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { IBoardBadgeInfo } from "Agile/Board/Badge/Models/BoardBadgeInfo";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { IWorkRestClient } from "Wit/Clients/Work/RestClient/Work";
import { BoardBadgeColumnOptions } from "Wit/Clients/Work/Contracts/Work";
import * as Resources from "Agile/Board/Badge/Resources";

const BoardBadgeIsPublicOptionKey: string = "statusBadgeIsPublic";
const BoardBadgeMarkdownTemplate: string = "[![{0}]({1})]({2})"; // 0 = alt text; 1 = badge url; 2 = board url
const BoardRouteId: string = "ms.vss-work-web.team-board-content-route"; // {project}/_boards/{pivot}/t/{teamName}/{backlogLevel}
const BoardPivotName: string = "board";
const ServiceInstanceType = "00025394-6065-48ca-87d9-7f5672854ef7";

/**
 * Board badge service
 */
export interface IBoardBadgeService extends IVssService {
    /**
     * Sets IsPublic option of board badge
     * @param teamContext
     * @param boardId
     * @param isPublic
     */
    setIsPublicOption(projectId: string, teamId: string, boardId: string, isPublic: boolean): Promise<boolean>;

    /**
     * Gets board badge info
     */
    getBoardBadgeInfo(
        projectId: string,
        teamId: string,
        backlogLevel: string,
        boardId: string,
        columnOption?: BoardBadgeColumnOptions,
        columns?: string[]
    ): IBoardBadgeInfo;
}

export class BoardBadgeService extends VssService implements IBoardBadgeService {
    public async setIsPublicOption(projectId: string, teamId: string, boardId: string, isPublic: boolean): Promise<boolean> {
        const workRestClient = this.pageContext.getRestClient<IWorkRestClient>("IWorkRestClient");
        const updatedOptions = await workRestClient.setBoardOptions(
            { [BoardBadgeIsPublicOptionKey]: `${isPublic}` },
            { projectId, teamId } as TeamContext,
            boardId
        );
        return (updatedOptions && updatedOptions[BoardBadgeIsPublicOptionKey]) === "true";
    }

    public getBoardBadgeInfo(
        projectId: string,
        teamId: string,
        backlogLevel: string,
        boardId: string,
        columnOptions?: BoardBadgeColumnOptions,
        columns?: string[]
    ): IBoardBadgeInfo {
        const boardUrl = this._getBoardUrl(projectId, teamId, backlogLevel);
        const badgeUrl = this._getBoardBadgeUrl(projectId, teamId, boardId, columnOptions, columns);
        const badgeMarkdown = format(
            BoardBadgeMarkdownTemplate,
            Resources.BoardBadge_AltText,
            this._escapeForMarkdown(badgeUrl),
            this._escapeForMarkdown(boardUrl)
        );
        return {
            badgeUrl,
            badgeMarkdown
        };
    }

    private _getBoardBadgeUrl(
        projectId: string,
        teamId: string,
        boardId: string,
        columnOption?: BoardBadgeColumnOptions,
        columns?: string[]
    ): string {
        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();
        // Page data is always available but set a default in case we don't blow away the entire UI.
        const isHosted = pageData ? pageData.isHosted : true;

        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        const serviceLocation = locationService.getServiceLocation(ServiceInstanceType);
        // Service location returns "/[Collection]/" on onprem but returns "http://dev.azure.com/[account]" on hosted.
        const boardBadgeUrl = `${isHosted ? "" : location.origin}${serviceLocation}${projectId}/${teamId}/_apis/work/boardbadge/${boardId}`;

        if (columnOption) {
            const customColumns = columnOption === BoardBadgeColumnOptions.CustomColumns && columns ? `&columns=${columns.join(",")}` : "";
            return `${boardBadgeUrl}?columnOptions=${columnOption}${customColumns}`;
        } else {
            return boardBadgeUrl;
        }
    }

    private _getBoardUrl(projectId: string, teamId: string, backlogLevelId: string): string {
        const routeValues: { [key: string]: string } = {
            project: projectId,
            pivot: BoardPivotName,
            teamName: teamId,
            backlogLevel: backlogLevelId
        };
        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        const routeUrl = locationService.routeUrl(BoardRouteId, routeValues);
        return `${location.origin}${routeUrl}`;
    }

    private _escapeForMarkdown(url: string): string {
        // Url in markdown should escape all parenthesis and spaces
        return url.replace(/ /g, "%20").replace(/(\[|\]|\(|\))/g, "\\$&"); // CodeQL [SM02383] This method is not intended to be used with user input.
    }
}

Services.add("IBoardBadgeService", { serviceFactory: BoardBadgeService });
